/**
 * Purpose: User-interface strings for the copilot widget, with built-in
 *          English and Spanish catalogs, language resolution, and a tiny
 *          `{placeholder}` formatter. Lets the widget follow the host
 *          page's language (`<html lang="es">`) instead of always showing
 *          English, and lets hosts override any individual string.
 *
 * Implementation notes:
 *   - Catalog selection uses only the primary subtag (`es-ES` → `es`).
 *     Unknown languages fall back to English. Hosts that need another
 *     language pass a full `strings` override (every key is a plain
 *     string, so it round-trips through a JSON DOM attribute).
 *   - Strings are templates with `{name}` placeholders, filled by
 *     {@link formatString}. Unknown placeholders are left verbatim so a
 *     typo in a host override is visible rather than silently blank.
 *   - Pluralisation is limited to the one counted string (history
 *     message count) and uses `Intl.PluralRules` to pick `…One` /
 *     `…Other`. Relative times use `Intl.RelativeTimeFormat`, so they
 *     need no catalog entries at all.
 *   - Language resolution order: explicit `lang` → `<html lang>` →
 *     `navigator.language` → `'en'`. The CDN element does its own,
 *     richer lookup (nearest `[lang]` ancestor of the element) and
 *     passes the result down as the explicit value.
 *
 * Created: 2026-09-25 08:00
 *
 * Changelog:
 *   - 2026-09-25 08:00 — Initial version. Localize the widget UI (en + es)
 *                        after the Celestina embed showed English chrome
 *                        around a Spanish conversation.
 *   - 2026-09-25 08:27 — `formatRelativeTime` falls back to a plain date when
 *                        `Intl.RelativeTimeFormat` is missing (Safari < 14).
 *   - 2026-10-06 17:15 — Add the `talk*` strings for the opt-in in-chat
 *                        realtime voice mode (`voice="realtime"`): tooltip,
 *                        connecting / answering states, Interrupt button,
 *                        interrupted-reply marker and error lines.
 */
const EN = {
    defaultTitle: 'Copilot',
    inputPlaceholder: 'Type a question…',
    inputPlaceholderWithFiles: 'Type a question or drop a file…',
    inputPlaceholderDictating: 'Listening… speak now',
    inputAriaLabel: 'Message',
    send: 'Send',
    stop: 'Stop',
    stopAria: 'Stop the reply',
    enterHint: 'Enter to send · Shift+Enter for a new line',
    dictate: 'Dictate',
    dictateTooltip: 'Dictate: speak and your words are typed into the box. Nothing is sent until you press Send.',
    dictateStop: 'Stop dictating',
    voiceChat: 'Talk',
    voiceChatTooltip: 'Voice conversation: speak, and the reply is read aloud. It keeps listening until you end it.',
    voiceChatEnd: 'End',
    voiceChatListening: 'Listening…',
    voiceChatThinking: 'Thinking…',
    voiceChatSpeaking: 'Answering aloud…',
    voiceChatActive: 'Voice conversation',
    talkTooltip: 'Real-time voice conversation: speak naturally. You can interrupt the answer just by talking.',
    talkConnecting: 'Connecting…',
    talkSpeaking: 'Answering…',
    talkInterrupt: 'Interrupt',
    talkInterruptTitle: 'Cut the answer short. You can also just start talking.',
    talkInterrupted: 'interrupted',
    talkIdleEnded: 'The voice conversation ended after a while without activity.',
    talkStartFailed: 'Could not start the voice conversation: {error}',
    talkDropped: 'The voice conversation was cut off. Press Talk to resume it.',
    talkSessionError: 'Voice conversation error: {error}',
    speakerOn: 'Stop reading replies aloud',
    speakerOff: 'Read replies aloud',
    history: 'Conversation history',
    back: 'Back to chat',
    newConversation: 'New conversation',
    close: 'Close',
    collapse: 'Collapse panel',
    expand: 'Expand {title}',
    open: 'Open {title}',
    resize: 'Resize panel',
    emptyState: 'Ask anything about this app — how to get started, where to find a setting, or what something means.',
    suggestionsLabel: 'Try asking',
    thinking: 'Thinking…',
    toolWorking: 'Working on it…',
    stopped: 'Reply stopped.',
    historyLoading: 'Loading…',
    historyEmpty: 'No saved conversations yet. Start chatting and they will show up here.',
    historyCountOne: '1 message',
    historyCountOther: '{count} messages',
    conversationNotFound: 'Conversation not found.',
    untitledConversation: 'New conversation',
    persistFailed: 'Kept in this window only — the conversation could not be saved: {error}',
    genericError: 'Something went wrong: {error}',
    attachFile: 'Attach a file',
    removeFile: 'Remove {name}',
    attachedFiles: 'Attached files',
    filesTooLarge: 'Skipped {names} — files must be {limit} or smaller.',
    modelLabel: 'Model',
    modelFilter: 'Filter models…',
    modelListUnavailable: 'Model list unavailable.',
    modelNoMatch: 'No models match “{query}”.',
    voiceErrorNotAllowed: 'The browser blocked the microphone. Allow microphone access for this site to use voice.',
    voiceErrorNoMic: 'No microphone was found.',
    voiceErrorNetwork: 'Voice recognition needs an internet connection.',
    voiceErrorGeneric: 'Voice input error: {error}',
};
const ES = {
    defaultTitle: 'Asistente',
    inputPlaceholder: 'Escribe tu pregunta…',
    inputPlaceholderWithFiles: 'Escribe tu pregunta o suelta un archivo…',
    inputPlaceholderDictating: 'Te escucho… habla ahora',
    inputAriaLabel: 'Mensaje',
    send: 'Enviar',
    stop: 'Detener',
    stopAria: 'Detener la respuesta',
    enterHint: 'Intro para enviar · Mayús+Intro para nueva línea',
    dictate: 'Dictar',
    dictateTooltip: 'Dictar: habla y lo que digas se escribe en el cuadro. No se envía nada hasta que pulses Enviar.',
    dictateStop: 'Terminar el dictado',
    voiceChat: 'Conversar',
    voiceChatTooltip: 'Conversación por voz: habla y te responde en voz alta. Sigue escuchando hasta que la termines.',
    voiceChatEnd: 'Terminar',
    voiceChatListening: 'Te escucho…',
    voiceChatThinking: 'Pensando…',
    voiceChatSpeaking: 'Respondiendo en voz alta…',
    voiceChatActive: 'Conversación por voz',
    talkTooltip: 'Conversación por voz en tiempo real: habla con naturalidad. Puedes interrumpir la respuesta simplemente hablando.',
    talkConnecting: 'Conectando…',
    talkSpeaking: 'Respondiendo…',
    talkInterrupt: 'Interrumpir',
    talkInterruptTitle: 'Corta la respuesta. También puedes interrumpirla simplemente hablando.',
    talkInterrupted: 'interrumpida',
    talkIdleEnded: 'La conversación por voz se cerró por inactividad.',
    talkStartFailed: 'No se pudo iniciar la conversación por voz: {error}',
    talkDropped: 'Se cortó la conversación por voz. Pulsa Conversar para retomarla.',
    talkSessionError: 'Error en la conversación por voz: {error}',
    speakerOn: 'Dejar de leer las respuestas en voz alta',
    speakerOff: 'Leer las respuestas en voz alta',
    history: 'Historial de conversaciones',
    back: 'Volver al chat',
    newConversation: 'Nueva conversación',
    close: 'Cerrar',
    collapse: 'Contraer el panel',
    expand: 'Desplegar {title}',
    open: 'Abrir {title}',
    resize: 'Cambiar el tamaño del panel',
    emptyState: 'Pregúntame lo que quieras sobre esta aplicación: cómo empezar, dónde está una opción o qué significa algo.',
    suggestionsLabel: 'Puedes preguntar',
    thinking: 'Pensando…',
    toolWorking: 'Consultando los datos…',
    stopped: 'Respuesta detenida.',
    historyLoading: 'Cargando…',
    historyEmpty: 'Todavía no hay conversaciones guardadas. Empieza a chatear y aparecerán aquí.',
    historyCountOne: '1 mensaje',
    historyCountOther: '{count} mensajes',
    conversationNotFound: 'No se ha encontrado la conversación.',
    untitledConversation: 'Nueva conversación',
    persistFailed: 'Solo se conserva en esta ventana: no se ha podido guardar la conversación ({error}).',
    genericError: 'Algo ha fallado: {error}',
    attachFile: 'Adjuntar un archivo',
    removeFile: 'Quitar {name}',
    attachedFiles: 'Archivos adjuntos',
    filesTooLarge: 'Se han omitido {names}: cada archivo puede ocupar {limit} como máximo.',
    modelLabel: 'Modelo',
    modelFilter: 'Filtrar modelos…',
    modelListUnavailable: 'La lista de modelos no está disponible.',
    modelNoMatch: 'Ningún modelo coincide con «{query}».',
    voiceErrorNotAllowed: 'El navegador ha bloqueado el micrófono. Permite el acceso al micrófono en este sitio para usar la voz.',
    voiceErrorNoMic: 'No se ha encontrado ningún micrófono.',
    voiceErrorNetwork: 'El reconocimiento de voz necesita conexión a internet.',
    voiceErrorGeneric: 'Error en la entrada de voz: {error}',
};
const CATALOGS = { en: EN, es: ES };
/** Languages with a built-in catalog (primary subtags). */
export const BUILT_IN_LANGUAGES = Object.keys(CATALOGS);
/**
 * Resolve the BCP-47 tag the widget should use. `explicit` wins; then the
 * document's `<html lang>`; then the browser language; then `'en'`.
 */
export function resolveLanguage(explicit) {
    const fromExplicit = explicit?.trim();
    if (fromExplicit)
        return fromExplicit;
    if (typeof document !== 'undefined') {
        const htmlLang = document.documentElement.getAttribute('lang')?.trim();
        if (htmlLang)
            return htmlLang;
    }
    if (typeof navigator !== 'undefined' && navigator.language)
        return navigator.language;
    return 'en';
}
/**
 * Build the effective string table for `lang`: the built-in catalog for
 * its primary subtag (English when unknown), with `overrides` on top.
 * Non-string override values are ignored so a malformed JSON attribute
 * cannot blank out the UI.
 */
export function buildStrings(lang, overrides) {
    const primary = lang.toLowerCase().split(/[-_]/)[0] ?? 'en';
    const base = CATALOGS[primary] ?? EN;
    if (!overrides)
        return base;
    const merged = { ...base };
    for (const key of Object.keys(overrides)) {
        const value = overrides[key];
        if (typeof value === 'string' && key in base)
            merged[key] = value;
    }
    return merged;
}
/** Replace `{name}` placeholders in `template` with `vars[name]`. */
export function formatString(template, vars = {}) {
    return template.replace(/\{(\w+)\}/g, (match, name) => name in vars ? String(vars[name]) : match);
}
/** "3 messages" / "3 mensajes", pluralised for `lang`. */
export function formatMessageCount(strings, lang, count) {
    let category = count === 1 ? 'one' : 'other';
    try {
        category = new Intl.PluralRules(lang).select(count);
    }
    catch {
        // Invalid tag — keep the naive English rule above.
    }
    const template = category === 'one' ? strings.historyCountOne : strings.historyCountOther;
    return formatString(template, { count });
}
/** Localized relative time ("5 minutes ago" / "hace 5 minutos"). */
export function formatRelativeTime(iso, lang) {
    const then = new Date(iso).getTime();
    if (!Number.isFinite(then))
        return '';
    const seconds = Math.round((then - Date.now()) / 1000);
    const abs = Math.abs(seconds);
    let rtf = null;
    try {
        rtf = new Intl.RelativeTimeFormat(lang, { numeric: 'auto' });
    }
    catch {
        try {
            rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
        }
        catch {
            // No Intl.RelativeTimeFormat at all (Safari < 14): dates only.
        }
    }
    if (!rtf)
        return new Date(iso).toLocaleDateString();
    if (abs < 60)
        return rtf.format(0, 'second');
    if (abs < 3600)
        return rtf.format(Math.round(seconds / 60), 'minute');
    if (abs < 86_400)
        return rtf.format(Math.round(seconds / 3600), 'hour');
    if (abs < 7 * 86_400)
        return rtf.format(Math.round(seconds / 86_400), 'day');
    try {
        return new Date(iso).toLocaleDateString(lang);
    }
    catch {
        return new Date(iso).toLocaleDateString();
    }
}
//# sourceMappingURL=i18n.js.map